import type { WorkRow } from '@workflow/contracts'
import { EmptyNote, StatusLabel } from '@workflow/ui'
import { formatTimestamp } from '@/lib/format'
import { PAYMENT_STATUS, partState, formatHours, formatMoney } from '@/features/jobs/jobsStatus'
import { WorkDescription } from '@/features/jobs/WorkDescription'

/**
 * One row of a person's work record, as either reader sees it: the job, who
 * it was for, the hours, the state of the work, the state of the
 * pay — and the money words only when they were meant to be read. The row
 * does not know which reader holds it; the serializer already decided.
 */
export function WorkRowLine({ row, staff }: { row: WorkRow; staff?: boolean }) {
  const state = partState(row)
  const pay = PAYMENT_STATUS[row.paymentStatus]
  return (
    <li className="border-b border-hairline last:border-0">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-1 py-2.5">
        <span className="min-w-0 flex-1">
          <span className="block truncate text-ui font-medium text-foreground">{row.jobTitle}</span>
          <span className="block truncate text-caption text-muted-foreground">
            {[row.clientName ?? 'Internal work', `${formatHours(row.actualHours)} worked`].join(' · ')}
          </span>
        </span>
        <StatusLabel tone={state.tone} hue={state.hue} label={state.label} />
        <StatusLabel tone={pay.tone} label={pay.label} />
        {row.rate !== null ? (
          <span className="text-caption text-muted-foreground" title="Your rate">
            {formatMoney(row.rate.amount)} {row.rate.currency} {row.rate.type === 'hourly' ? 'per hour' : 'for the piece'}
          </span>
        ) : staff && row.paymentAmount !== null ? (
          <span className="text-caption text-muted-foreground">
            {formatMoney(row.paymentAmount)} on the record
          </span>
        ) : null}
        {row.quality !== null ? (
          <span className="text-caption text-muted-foreground" title={row.qualityNotes ?? undefined}>
            {row.quality} / 5
          </span>
        ) : null}
      </div>
      {row.completedAt !== null ? (
        <p className="px-1 pb-2 text-caption text-muted-foreground">Finished {formatTimestamp(row.completedAt)}</p>
      ) : null}
      <WorkDescription description={row.jobDescription} className="px-1 pb-3" />
    </li>
  )
}

/** The record's two halves: what is live, and what is past. */
export function WorkRows({ rows, staff, emptyDetail }: { rows: WorkRow[]; staff?: boolean; emptyDetail: string }) {
  const live = rows.filter((row) => row.completion === 'assigned' || row.completion === 'in_progress')
  const past = rows.filter((row) => row.completion !== 'assigned' && row.completion !== 'in_progress')
  if (rows.length === 0) return <EmptyNote title="No work on file yet." detail={emptyDetail} />
  return (
    <div className="flex flex-col gap-5">
      <section>
        <h4 className="text-micro mb-1 text-muted-foreground uppercase">Now</h4>
        {live.length === 0 ? (
          <p className="px-1 text-caption text-muted-foreground">Nothing assigned right now.</p>
        ) : (
          <ul className="flex flex-col">
            {live.map((row) => (
              <WorkRowLine key={row.assignmentId} row={row} staff={staff} />
            ))}
          </ul>
        )}
      </section>
      {past.length > 0 ? (
        <section>
          <h4 className="text-micro mb-1 text-muted-foreground uppercase">History</h4>
          <ul className="flex flex-col">
            {past.map((row) => (
              <WorkRowLine key={row.assignmentId} row={row} staff={staff} />
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  )
}
